import {
  AnalyticsEventName,
  DESKTOP_CHROME_WEB_STORE_URL,
  FeatureFlags,
} from '@dupe/common/constants'
import { VisualMatch } from '@dupe/common/types'
import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import { browserName, isChrome, isDesktop } from 'react-device-detect'
import VerifiedBadge from '~/src/components/VerifiedBadge'
import DupeIcon from '~/src/components/svg/DupeIcon'
import HeaderLogo from '~/src/components/svg/HeaderLogo'
import { useAuthContext } from '~/src/context/AuthProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'

const checkIfEbay = (shopKey: string | undefined) => {
  if (!shopKey) return false
  return shopKey.indexOf('ebay.') > -1
}

interface ExtensionFloatingButtonProps {
  dupesData?: VisualMatch[]
}

export const ExtensionFloatingButton = ({
  dupesData,
}: ExtensionFloatingButtonProps) => {
  const [showSidepanel, setShowSidepanel] = useState(false)
  const [showFallbackLogo, setShowFallbackLogo] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const analytics = useAnalytics()

  const displayedMatches = useMemo(
    () => (dupesData || []).slice(0, 4) || [],
    [dupesData]
  )

  // Extension Preview Feature Flags
  const {
    featureOn: isExtensionPreviewEnabled,
    loading: isExtensionPreviewLoading,
  } = useIsFeatureOn(FeatureFlags.ResultsPageExtensionPreview)

  // Extension Preview Sidepanel Feature Flag
  const isExtensionPreviewSidepanelEnabled = false
  // const {
  //   featureOn: isExtensionPreviewSidepanelEnabled,
  //   loading: isExtensionPreviewSidepanelLoading,
  // } = useIsFeatureOn(FeatureFlags.ResultsPageExtensionPreviewSidepanel)

  const eitherFeatureEnabled =
    isExtensionPreviewEnabled || isExtensionPreviewSidepanelEnabled

  const { extensionInstalled } = useAuthContext()

  const showExtensionCTA =
    isChrome && !extensionInstalled?.installed && isDesktop

  const isLoading = isExtensionPreviewLoading //|| isExtensionPreviewSidepanelLoading

  const closeSidepanel = () => {
    setShowSidepanel(false)
  }

  const tabButtonPressed = () => {
    // Check if sidepanel feature flag is enabled
    const useSidepanel = isExtensionPreviewSidepanelEnabled

    // Track event
    analytics.track(AnalyticsEventName.ResultsExtensionTabPressed, {
      browser: browserName,
      useSidepanel,
    })

    if (useSidepanel) {
      setShowSidepanel(true)
    } else {
      // open web store link in new tab
      window.open(DESKTOP_CHROME_WEB_STORE_URL, '_blank')
    }
  }

  const [showTooltip, setShowTooltip] = useState(false)

  const canDisplayExtensionCTA =
    eitherFeatureEnabled &&
    showExtensionCTA &&
    !isLoading &&
    !!displayedMatches.length

  useEffect(() => {
    if (!canDisplayExtensionCTA) return

    const showTimer = setTimeout(() => {
      setShowTooltip(true)
    }, 1500)

    return () => {
      clearTimeout(showTimer)
    }
  }, [canDisplayExtensionCTA])

  if (!canDisplayExtensionCTA) {
    return null
  }

  if (showSidepanel) {
    return (
      <div className='fixed right-0 top-0 z-10 flex h-screen w-[373px] flex-col border-l border-l-[#e1e3e1] bg-white font-sans'>
        <div className='flex h-full flex-col'>
          <div className='mt-1 flex flex-row items-center justify-between px-3'>
            <div className='flex min-w-0 flex-1 flex-row items-center pr-2'>
              <Image
                src='/assets/icon-shop.png'
                className='h-4 w-4 flex-shrink-0'
                width={16}
                height={16}
                alt='Dupe.com Browser Extension'
              />
              <span className='ml-1.5 truncate text-sm font-semibold text-gray-800'>
                Install Dupe.com Extension
              </span>
            </div>
            <div className='flex-shrink-0'>
              <button
                onClick={closeSidepanel}
                className='rounded-full p-1.5 opacity-60 transition-opacity hover:bg-gray-100 hover:opacity-100'
              >
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                  <path
                    d='M12 4L4 12M4 4L12 12'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Link intentionally wrapping the entire sidepane content */}
          <Link
            href={DESKTOP_CHROME_WEB_STORE_URL}
            target='_blank'
            rel='noopener noreferrer'
            className='flex-1'
            onClick={() => {
              analytics.track(
                AnalyticsEventName.ResultsExtensionWebStoreLinkClicked,
                {
                  browser: browserName,
                  useSidepanel: isExtensionPreviewSidepanelEnabled,
                }
              )
            }}
          >
            <div className='m-1.5 mt-1 flex h-full flex-col overflow-hidden rounded-[15px] bg-[#2d2f34] shadow-xl'>
              {/* Header */}
              <div className='flex flex-row items-center justify-between px-4 py-3'>
                <HeaderLogo />
              </div>

              {/* Content */}
              <div className='flex h-full flex-col'>
                <div className='relative z-50 h-0'>
                  <div className='absolute left-0 top-0 h-[20px] w-full bg-gradient-to-b from-[#2d2f34] from-40% to-transparent opacity-100 transition-opacity duration-200' />
                </div>
                <div
                  ref={containerRef}
                  className={clsx(
                    'mb-10 flex-1 overflow-y-auto px-6 pb-6 pt-3'
                  )}
                >
                  <div className='flex flex-col'>
                    <h2 className='mb-2 text-xl font-semibold text-white'>
                      Find similar products for less
                    </h2>
                    <p className='text-sm text-gray-300'>
                      Install the Dupe extension to find similar products and
                      the best deals across the web.
                    </p>

                    <div className='my-4 rounded-lg bg-[#B0F6FF]/20 p-4'>
                      <div className='mb-2 flex items-center gap-2'>
                        <Image
                          src='/assets/activity/single-coin.png'
                          alt=''
                          width={23}
                          height={23}
                        />
                        <span className='text-lg font-semibold text-[#B0F6FF]'>
                          Earn $DUPE
                        </span>
                      </div>
                      <p className='text-sm text-gray-300'>
                        When you install our desktop extension
                      </p>
                    </div>

                    <div className='relative space-y-4'>
                      <div className='absolute bottom-0 left-0 z-10 h-[310px] w-full bg-gradient-to-b from-transparent to-[#2d2f34] to-65% opacity-100 transition-opacity duration-200' />

                      {displayedMatches.map((match) => (
                        <div key={match.link || ''}>
                          <div className='flex items-center gap-4 rounded-lg border border-[#B0F6FF]/20 p-4'>
                            <div className='relative h-16 w-16 overflow-hidden rounded-lg'>
                              <Image
                                unoptimized
                                src={match.image || ''}
                                alt={match.title || ''}
                                width={64}
                                height={64}
                                className='block h-full w-full object-cover transition-all hover:scale-105'
                                onContextMenu={(e) => e.preventDefault()}
                              />
                            </div>
                            <div className='flex-1 space-y-2'>
                              <div className='flex items-center space-x-1'>
                                <div className='overflow-hidden rounded-full border bg-gray-100 dark:border-white/50 dark:bg-black'>
                                  <Image
                                    src={
                                      match.source_icon ||
                                      `https://ui-avatars.com/api/?name=${match.source}`
                                    }
                                    alt={match.source || ''}
                                    unoptimized
                                    width={16}
                                    height={16}
                                    className='h-3 w-3 rounded-full bg-white object-contain md:h-4 md:w-4 dark:bg-black'
                                  />
                                </div>
                                <div className='truncate text-sm font-medium text-white opacity-90 md:text-base'>
                                  {match.source}
                                </div>
                                {match.verified && (
                                  <VerifiedBadge shopKey={match.shopKey} />
                                )}
                                {checkIfEbay(match.shopKey) && (
                                  <div className='text-sm text-white opacity-60'>
                                    #sponsored
                                  </div>
                                )}
                                {!!match?.extra_icon && (
                                  <Image
                                    src={match.extra_icon}
                                    alt={match.source || ''}
                                    unoptimized
                                    width={66}
                                    height={12}
                                    className='h-3 opacity-80 invert'
                                  />
                                )}
                              </div>
                              <div className='line-clamp-2 text-sm font-medium text-white'>
                                {match.title}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className='z-20 -mt-[80px] flex flex-row items-center justify-center gap-2'>
                      <div className='relative flex w-full flex-row items-center justify-center gap-2 rounded-lg border-2 border-[#B0F6FF] border-transparent bg-[#B0F6FF] p-2 text-sm font-semibold text-black transition-all'>
                        <div
                          style={{
                            backgroundImage: `url('/assets/apps/chromium.png')`,
                            backgroundSize: 'cover',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'top left',
                          }}
                          className='h-[28px] w-[28px]'
                        />
                        Add to Chrome - It&apos;s Free
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div
      className='absolute right-[10px] top-[270px] z-10 m-0 hidden p-0 font-sans [@media(min-width:895px)]:block'
      onClick={() => tabButtonPressed()}
    >
      <div
        className={clsx(
          'absolute right-[60px] top-1/2 hidden -translate-y-1/2 transition-opacity duration-100 ease-linear [@media(min-width:1210px)]:block',
          showTooltip ? 'opacity-100' : 'opacity-0'
        )}
      >
        <div className='w-[150px] rounded-lg bg-black px-3 py-2 text-sm font-medium text-white'>
          Try our desktop extension!
        </div>
        <div
          className='absolute right-[-8px] top-1/2 h-0 w-0 -translate-y-1/2
          border-y-8 border-l-8 border-y-transparent border-l-black'
          aria-hidden='true'
        />
      </div>

      <div
        className={clsx(
          'relative flex h-[50px] w-[50px] cursor-pointer items-center justify-center rounded-lg border-2 border-[#2d2f34] bg-[#2d2f34] transition-all dark:bg-black'
        )}
        style={{
          boxShadow: '0px 0px 1px #4B4D52, 0px 3px 6px rgba(75, 77, 82, 0.2)',
        }}
      >
        <div
          style={{
            colorScheme: 'initial',
          }}
          className='flex h-full w-full flex-1 items-center justify-center p-1'
        >
          <iframe
            style={{
              pointerEvents: 'none',
              display: showFallbackLogo ? 'none' : 'block',
            }}
            onError={() => {
              setShowFallbackLogo(true)
            }}
            onLoad={() => {
              setShowFallbackLogo(false)
            }}
            className='mx-auto h-[42px] w-[42px]'
            src='https://dupe.com/lotti/mobile-safari/blue-tab.html'
          />
          {showFallbackLogo && (
            <DupeIcon
              style={{
                height: '37px',
                display: showFallbackLogo ? 'block' : 'none',
              }}
              color='#b0f6ff'
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default ExtensionFloatingButton
